import React, { useContext, useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import IRMA from "@models/IRMA";
import IRMA_Line_Item from "@models/IRMA_Line_Item";
import IRMA_Line_Item_Inspection from "@models/IRMA_Line_Item_Inspection";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwTextArea from "@components/shared/OwTextArea/OwTextArea";
import OwCheckbox from "@components/shared/OwCheckbox/OwCheckbox";
import { OwSelectOptionFn } from "@components/shared/OwSelectOptionFn/OwSelectOptionFn";
import RmaService from "@services/RmaService";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import RmaConditionsOnReceiptEnum from "@models/RmaConditionsOnReceiptEnum";
import "./EditInspectionModal.scss";
import NotificationLevelEnum from "@models/NotificationLevelEnum";

type Props = {
    rma: IRMA;
    lineItem: IRMA_Line_Item | null;
    inspection: IRMA_Line_Item_Inspection | null;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
};

export const EditInspectionModal = (props: Props) => {
    const { rma, lineItem, inspection, isOpen, onClose, onSuccess } = props;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);

    const [conditionId, setConditionId] = useState<number | null>(null);
    const [quantity, setQuantity] = useState<number>(0);
    const [restockable, setRestockable] = useState<boolean>(false);
    const [restockWarehouseId, setRestockWarehouseId] = useState<number | null>(null);
    const [notes, setNotes] = useState<string>("");
    const [refundAmount, setRefundAmount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    const conditionOptions = [
        { value: RmaConditionsOnReceiptEnum.GOOD, label: "Good" },
        { value: RmaConditionsOnReceiptEnum.DAMAGED, label: "Damaged" },
        { value: RmaConditionsOnReceiptEnum.UNUSABLE, label: "Unusable" },
        { value: RmaConditionsOnReceiptEnum.NOT_RECEIVED, label: "Not Received" },
    ];

    const warehouseOptions = rma.Warehouse ? [{ value: rma.Warehouse.id, label: rma.Warehouse.name }] : [];

    useEffect(() => {
        if (isOpen && inspection) {
            setConditionId(inspection.condition_on_receipt_id);
            setQuantity(inspection.quantity);
            setRestockable(inspection.restockable);
            setRestockWarehouseId(inspection.restock_warehouse_id || null);
            setNotes(inspection.inspection_notes || "");
            setRefundAmount(inspection.refund_amount || 0);
        }
    }, [isOpen, inspection]);

    const handleSubmit = async () => {
        if (!lineItem || !inspection) return;

        // Validation
        if (!conditionId) {
            uiContext.showNotification({
                message: "Please select a condition",
                level: NotificationLevelEnum.error,
            });
            return;
        }
        if (quantity <= 0) {
            uiContext.showNotification({
                message: "Quantity must be greater than 0",
                level: NotificationLevelEnum.error,
            });
            return;
        }
        if (restockable && !restockWarehouseId && supplierModel.has_warehouses) {
            uiContext.showNotification({
                message: "Please select a warehouse for restockable items",
                level: NotificationLevelEnum.error,
            });
            return;
        }

        try {
            setLoading(true);
            await RmaService.updateInspection(
                rma.guid,
                lineItem.guid,
                inspection.guid,
                {
                    condition_id: conditionId,
                    quantity,
                    restockable,
                    restock_warehouse_id: restockWarehouseId || undefined,
                    notes: notes || undefined,
                    refund_amount: refundAmount || undefined,
                },
                userModel,
                supplierModel
            );

            uiContext.showNotification({
                message: "Inspection updated successfully",
                level: NotificationLevelEnum.success,
            });
            onSuccess();
            onClose();
        } catch (err) {
            uiContext.showNotification({
                message: err instanceof Error ? err.message : "Failed to update inspection",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setLoading(false);
        }
    };

    if (!lineItem || !inspection) return null;

    return (
        <Modal show={isOpen} onHide={onClose} size="lg" className="EditInspectionModal" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <OwLabel title="edit_inspection" /> - {lineItem.sku} {lineItem.catalog_item_name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row">
                    <div className="col-md-6">
                        <OwLabel title="condition" isBold={true} />
                        <OwSelectOptionFn
                            name="conditionId"
                            selectedOption={conditionId}
                            valueProp="value"
                            titleProp="label"
                            fn={(e: any) => setConditionId(parseInt(e.target.value))}
                            options={conditionOptions}
                            hasSelectedOptionProp={false}
                        />
                    </div>
                    <div className="col-md-6">
                        <OwLabel title="quantity" isBold={true} />
                        <OwInputWrapper
                            name="quantity"
                            type="number"
                            value={quantity.toString()}
                            fn={(e: any) => setQuantity(parseInt(e.target.value) || 0)}
                            min={0}
                            max={lineItem.qty_received || 0}
                        />
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-md-6">
                        <OwCheckbox
                            name="restockable"
                            label="restockable"
                            checked={restockable}
                            fn={(e: any) => setRestockable(e.target.checked)}
                        />
                    </div>
                    {restockable && supplierModel.has_warehouses && (
                        <div className="col-md-6">
                            <OwLabel title="warehouse" isBold={true} />
                            <OwSelectOptionFn
                                name="restockWarehouseId"
                                selectedOption={restockWarehouseId}
                                valueProp="id"
                                titleProp="name"
                                fn={(e: any) => setRestockWarehouseId(parseInt(e.target.value))}
                                options={warehouseOptions}
                                hasSelectedOptionProp={false}
                            />
                        </div>
                    )}
                </div>

                <div className="row mt-3">
                    <div className="col-md-6">
                        <OwLabel title="refund_amount" isBold={true} />
                        <OwInputWrapper
                            name="refundAmount"
                            type="number"
                            step={0.01}
                            value={refundAmount.toString()}
                            fn={(e: any) => setRefundAmount(parseFloat(e.target.value) || 0)}
                            min={0}
                        />
                    </div>
                    <div className="col-md-6">
                        <OwLabel title="notes" isBold={true} />
                        <OwTextArea name="notes" value={notes} fn={(e: any) => setNotes(e.target.value)} rows={3} />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <OwButton title="cancel" fn={onClose} active={loading} className="btn-secondary me-2" />
                <OwButton title="save" fn={handleSubmit} active={loading} className="btn-primary me-2" />
            </Modal.Footer>
        </Modal>
    );
};
